import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiClient } from '@/lib/axios';
import type { ApiResponse, ApprovalInboxItem as SharedInboxItem } from '@vayu-saathi/shared-types';
import type { ExpenseClaim } from '@/lib/expenses';

// The inbox also carries reimbursement claims the caller can act on (type 'EXPENSE',
// with the full claim in `expense`). Approve/reject those via /expenses/:id/…
export type ApprovalItemType = 'LEAVE' | 'REGULARISATION' | 'EXPENSE';

export type ApprovalInboxItem = Omit<SharedInboxItem, 'type'> & {
  type: ApprovalItemType;
  expense?: ExpenseClaim | undefined;
};

export function useApprovalInbox(type?: ApprovalItemType, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['approval-inbox', type],
    enabled: options?.enabled ?? true,
    queryFn: async () => {
      const res = await apiClient.get<ApiResponse<ApprovalInboxItem[]>>('/approval-inbox', {
        params: type ? { type } : undefined,
      });
      return res.data.data;
    },
  });
}

export function useApprovalInboxCount(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['approval-inbox', 'count'],
    enabled: options?.enabled ?? true,
    queryFn: async () => {
      const res = await apiClient.get<ApiResponse<{ count: number }>>('/approval-inbox/count');
      return res.data.data.count;
    },
    refetchInterval: 60_000,
    // Nav badge — never worth an error toast
    meta: { suppressErrorToast: true },
  });
}

// Caches are refreshed in onSettled, not onSuccess: after a 400/409 "already actioned"
// the list must update too, so the stale item disappears instead of inviting another try.
export function useApproveLeave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      action,
      remarks,
    }: {
      id: string;
      action: 'APPROVED' | 'REJECTED';
      remarks?: string;
    }) => apiClient.patch(`/leaves/${id}/approve`, { action, ...(remarks ? { remarks } : {}) }),
    onSuccess: (_, { action }) => {
      toast.success(action === 'APPROVED' ? 'Leave approved' : 'Leave rejected');
    },
    onSettled: () => {
      // Approval moves pending → used on the balance and changes what the employee sees
      for (const key of [
        ['approval-inbox'],
        ['leaves'],
        ['leave-balances'],
        ['my-leaves'],
        ['dashboard'],
      ]) {
        void qc.invalidateQueries({ queryKey: key });
      }
    },
    meta: { errorMessage: 'Failed to update the leave request' },
  });
}

export function useApproveRegularisation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      action,
      remarks,
    }: {
      id: string;
      action: 'APPROVED' | 'REJECTED';
      remarks?: string;
    }) =>
      apiClient.patch(`/regularisations/${id}/review`, { action, ...(remarks ? { remarks } : {}) }),
    onSuccess: (_, { action }) => {
      toast.success(action === 'APPROVED' ? 'Regularisation approved' : 'Regularisation rejected');
    },
    onSettled: () => {
      // Approving rewrites the attendance row; same set as useReviewRegularisation
      for (const key of [['approval-inbox'], ['regularisations'], ['attendance'], ['dashboard']]) {
        void qc.invalidateQueries({ queryKey: key });
      }
    },
    meta: { errorMessage: 'Failed to update the regularisation request' },
  });
}
